import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CashDTextField from "components/common/CashDTextfield";
import CashDSelect from "components/common/CashDSelect";
import { useForm, Controller } from "react-hook-form";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import T from "utils/constants/T";
import { FUNDING_TYPE_OPTIONS } from "utils/constants/companyDetails";
import { addLender } from "store/reducers/lenderSettings";

const RenderTextField = (
  name: string,
  label: string,
  type: string,
  control: any,
  errors: any,
  isDisable = false,
  options = {}
) => {
  const { isProduction } = useSelector(
    (state: any) => state.lenderSettings,
    shallowEqual
  );

  const { envPrefix } = useMemo(() => {
    const envPrefix = isProduction ? "live_" : "test_";
    return { isProduction, envPrefix };
  }, [isProduction]);

  return (
    <Controller
      name={name}
      control={control}
      rules={options}
      render={({ field }) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let value = e.target.value.replace(/^\s+/, "");
          if (`${envPrefix}receivables_account_bsb` === name) {
            value = value.replace(/\D/g, "");
            if (value.length > 3)
              value = `${value.slice(0, 3)}-${value.slice(3, 6)}`;
          }
          if (type === "number") value = value.replace(/\D/g, "");
          field.onChange(value);
        };

        return (
          <CashDTextField
            {...field}
            fullWidth
            label={label}
            type={type}
            variant="outlined"
            sx={{ mb: 3 }}
            error={!!errors[name]}
            errorText={errors[name]?.message}
            onChange={handleChange}
            disabled={isDisable}
          />
        );
      }}
    />
  );
};

const RenderSelectField = (
  name: string,
  label: string,
  options: { label: string; value: string | number }[],
  control: any,
  errors: any,
  isDisable = false,
  rules = {}
) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    render={({ field }) => (
      <CashDSelect
        {...field}
        label={label}
        options={options}
        error={!!errors[name]}
        helperText={errors[name]?.message}
        disabled={isDisable}
        value={field.value || ""}
      />
    )}
  />
);

const FormTemplate = ({
  onSubmit,
  lenderAccount,
  formFields,
  extraFields = [],
  defaultValues,
  isViewEvent,
  isLenderAccountForm = true,
}: any) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: "all",
    defaultValues,
  });

  useEffect(() => {
    if (lenderAccount) reset(lenderAccount);
  }, [lenderAccount, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={900} fontSize={18}>
              {isLenderAccountForm ? "Monoova Config" : "Automatcher"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {extraFields.map(
              ({
                name,
                label,
                options,
                validation,
                isDisable,
              }: {
                name: string;
                label: string;
                options: any;
                validation: any;
                isDisable: boolean;
              }) => (
                <Box key={name}>
                  {RenderSelectField(
                    name,
                    label,
                    options,
                    control,
                    errors,
                    isDisable,
                    validation
                  )}
                </Box>
              )
            )}
            {formFields.map(
              ({
                name,
                label,
                type,
                validation,
                isDisable,
              }: {
                name: string;
                label: string;
                type: string;
                validation: any;
                isDisable: boolean;
              }) => (
                <Box key={name}>
                  {RenderTextField(
                    name,
                    label,
                    type,
                    control,
                    errors,
                    isDisable,
                    validation
                  )}
                </Box>
              )
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
      {!isViewEvent && (
        <Box mt={4} display="flex" justifyContent="end" gap={2}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            sx={{ width: "50%" }}
          >
            SUBMIT
          </Button>
        </Box>
      )}
    </form>
  );
};

const LenderAccountForm: React.FC<any> = ({
  lenderAccount,
  envPrefix,
  isProduction,
  onSubmit,
}) => {
  const initialLenderAccountValues = useMemo(
    () => ({
      lender_name: "",
      parent_id: null,
      [`${envPrefix}account_number`]: "",
      [`${envPrefix}api_key`]: "",
      ...(isProduction && { live_fee_account_number: "" }),
    }),
    [envPrefix, isProduction]
  );
  console.log("lenderAccount", lenderAccount, "envPrefix", envPrefix);

  const fields = useMemo(() => {
    return [
      {
        name: "lender_name",
        label: "Monoova Account Name",
        type: "text",
        validation: { required: "Please provide an account name." },
        isDisable: !!lenderAccount?.id,
      },
      {
        name: `${envPrefix}account_number`,
        label: "Monoova Account Number",
        type: "number",
        validation: { required: "Please provide an account number." },
        isDisable: !!lenderAccount?.id,
      },
      isProduction && {
        name: "live_fee_account_number",
        label: "Monoova Fee Account Number",
        type: "number",
        isDisable: !!lenderAccount?.id,
      },
      {
        name: `${envPrefix}api_key`,
        label: "API Key",
        type: "string",
        validation: {
          required: "Please provide an API key.",
          pattern: {
            value: /^.{16,}$/,
            message: "Api key must be at least 16 characters long.",
          },
        },
        isDisable: !!lenderAccount?.id,
      },
    ].filter(Boolean);
  }, [lenderAccount]);

  return (
    <FormTemplate
      onSubmit={onSubmit}
      lenderAccount={lenderAccount}
      formFields={fields}
      defaultValues={initialLenderAccountValues}
      isViewEvent={!!lenderAccount?.id}
    />
  );
};

const AutomatcherForm: React.FC<any> = ({
  lenderAccount,
  envPrefix,
  onSubmit,
}) => {
  const initialAutomatcherValues = {
    lender_name: "",
    parent_id: null,
    funding_type: "",
    [`${envPrefix}receivables_account_name`]: "",
    [`${envPrefix}receivables_account_number`]: "",
    [`${envPrefix}receivables_account_bsb`]: "",
  };

  const { superLenders } = useSelector(
    (state: any) => state.lenderSettings,
    shallowEqual
  );

  const accountTypeOptions = useMemo(() => {
    return (
      superLenders?.map(
        ({ lender_name, _id }: { lender_name: string; _id: string }) => ({
          label: lender_name,
          value: _id,
        })
      ) || []
    );
  }, [superLenders]);

  const fields = [
    {
      name: `${envPrefix}receivables_account_name`,
      label: "Automatcher Name",
      type: "text",
      validation: { required: "Please provide an account name." },
    },
    {
      name: `${envPrefix}receivables_account_number`,
      label: "Automatcher Account Number",
      type: "number",
      validation: { required: "Please provide an account number." },
    },
    {
      name: `${envPrefix}receivables_account_bsb`,
      label: "BSB (ex: 300-300)",
      type: "string",
      validation: {
        required: "Please provide a BSB.",
        pattern: {
          value: /^\d{3}-\d{3}$/,
          message: "BSB must be in XXX-XXX format.",
        },
      },
    },
  ];

  const extraFields = useMemo(() => {
    return [
      {
        name: "funding_type",
        label: "Select Funding Type",
        options: FUNDING_TYPE_OPTIONS,
        validation: { required: "Please select funding type." },
      },
      {
        name: "parent_id",
        label: "Select Monoova Account",
        type: "select",
        options: accountTypeOptions,
        validation: { required: "Please monoova account." },
      },
    ];
  }, [accountTypeOptions, FUNDING_TYPE_OPTIONS]);

  return (
    <FormTemplate
      onSubmit={onSubmit}
      lenderAccount={lenderAccount}
      formFields={fields}
      extraFields={extraFields}
      defaultValues={initialAutomatcherValues}
      isLenderAccountForm={false}
      isViewEvent={false}
    />
  );
};

const LenderCreationForm: React.FC<any> = ({ companyId, providerType }) => {
  const dispatch = useDispatch();
  const [isSuperLender, setIsSupperLender] = useState(true);

  const { lenderAccount, isProduction } = useSelector(
    (state: any) => state.lenderSettings,
    shallowEqual
  );

  console.log("lenderAccount", lenderAccount, isProduction);

  const { envPrefix } = useMemo(() => {
    const envPrefix = isProduction ? "live_" : "test_";
    return { isProduction, envPrefix };
  }, [isProduction]);

  const onSubmit = useCallback(
    async (data: any) => {
      const payload = {
        ...data,
        is_supper_lender: !!isSuperLender,
        provider_type: providerType,
        ...(!isSuperLender && { moonova_account_type: "SUPPER_LENDER" }),
      };
      dispatch(addLender(companyId, payload));
    },
    [isSuperLender, providerType, companyId]
  );

  return (
    <Box sx={{ width: 1 }}>
      {lenderAccount?.id && isSuperLender && (
        <Box display="flex" justifyContent="end" mb={2}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setIsSupperLender(false)}
          >
            {T.ADD_AUTOMATCHER}
          </Button>
        </Box>
      )}

      {isSuperLender ? (
        <LenderAccountForm
          lenderAccount={lenderAccount}
          isProduction={isProduction}
          envPrefix={envPrefix}
          onSubmit={onSubmit}
        />
      ) : (
        <AutomatcherForm
          lenderAccount={lenderAccount}
          envPrefix={envPrefix}
          onSubmit={onSubmit}
        />
      )}
    </Box>
  );
};

export default React.memo(LenderCreationForm);
